import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { DAYS_SHORT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calendar } from "lucide-react";
import { ImageUploader } from "@/components/ImageUploader";

const COLORS = ["#E8F5E9", "#E3F2FD", "#FFF3E0", "#F3E5F5", "#FCE4EC", "#E0F2F1"];
const defaultWork = () => { const h = {}; for (let d = 0; d < 7; d++) h[d] = { off: d >= 5, start: "09:00", end: "17:00" }; return h; };
const empty = () => ({ name: "", email: "", phone: "", role_title: "Stylist", color: COLORS[1], photo_url: null, bio: "", is_active: true, service_ids: [], working_hours: defaultWork(), days_off: [], login_password: "" });

export default function StaffPage() {
  const { business } = useAuth();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [dayOff, setDayOff] = useState("");

  const included = business?.subscription?.staff_included ?? 2;

  const load = useCallback(async () => { const { data } = await api.get("/staff"); setStaff(data); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setWork = (d, k, v) => setForm((f) => ({ ...f, working_hours: { ...f.working_hours, [d]: { ...f.working_hours[d], [k]: v } } }));
  const otherActive = staff.filter((s) => s.is_active && s.id !== editing?.id).length;
  const locked = !!business?.staff_hours_sync && otherActive > 0;
  const soloSync = !!business?.staff_hours_sync && otherActive === 0 && !!editing;
  const breaksOf = (d) => form.working_hours[d].breaks || [];
  const setBreaks = (d, list) => setWork(d, "breaks", list);
  const addBreak = (d) => setBreaks(d, [...breaksOf(d), { start: "12:00", end: "12:30" }]);
  const editBreak = (d, i, k, v) => setBreaks(d, breaksOf(d).map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const eveningOf = (d) => form.working_hours[d].evening || { on: false, start: "18:00", end: "21:00" };
  const setEvening = (d, k, v) => setWork(d, "evening", { ...eveningOf(d), [k]: v });
  const openNew = () => {
    const base = empty();
    if (business?.staff_hours_sync && staff.some((s) => s.is_active)) {
      const oh = business.opening_hours || {};
      base.working_hours = Object.fromEntries(Object.entries(base.working_hours).map(([d, w]) => {
        const b = oh[d] || {};
        return [d, { ...w, off: !!b.closed, start: b.open || w.start, end: b.close || w.end, evening: b.evening || { on: false } }];
      }));
    }
    setEditing(null); setForm(base); setOpen(true);
  };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty(), ...s, login_password: "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      if (editing) { delete payload.login_password; await api.put(`/staff/${editing.id}`, payload); }
      else { if (!payload.login_password) delete payload.login_password; await api.post("/staff", payload); }
      toast.success(editing ? "Staff updated" : "Staff added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this staff member?")) return; await api.delete(`/staff/${id}`); toast.success("Removed"); load(); };
  const addDayOff = () => { if (dayOff && !form.days_off.includes(dayOff)) { setForm((f) => ({ ...f, days_off: [...f.days_off, dayOff] })); setDayOff(""); } };

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><h1 className="font-extrabold text-3xl">Staff</h1>
          <p className="text-stone-600 text-sm mt-1">{staff.length} member{staff.length === 1 ? "" : "s"} · {included} included in your plan</p></div>
        <Button data-testid="add-staff-button" onClick={openNew} className="rounded-full bg-stone-900 hover:bg-stone-800"><Plus className="mr-1.5 h-4 w-4" /> Add staff</Button>
      </div>

      {staff.length >= included && (
        <div className="mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-amber-900">
          You're using all {included} included calendars. Additional staff can be added with paid seats (coming soon).
        </div>
      )}

      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full flex items-center justify-center font-semibold text-stone-700 overflow-hidden" style={{ background: s.color }}>{s.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${s.photo_url}`} alt="" className="h-full w-full object-cover" /> : s.name[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{s.name}</div><div className="text-xs text-stone-500">{s.role_title}</div></div>
              {!s.is_active && <span className="text-[10px] bg-stone-100 text-stone-500 rounded-full px-2 py-0.5">Off</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {DAYS_SHORT.map((d, i) => (
                <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${s.working_hours?.[i]?.off ? "bg-stone-100 text-stone-400" : "bg-emerald-50 text-emerald-700"}`}>{d}</span>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full flex-1" data-testid={`edit-staff-${s.id}`} onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
              <Button variant="ghost" size="sm" className="rounded-full text-rose-600" data-testid={`delete-staff-${s.id}`} onClick={() => remove(s.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
        {staff.length === 0 && <p className="text-sm text-stone-500">No staff yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit staff" : "Add staff"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name</Label><Input data-testid="staff-name" value={form.name} onChange={set("name")} className="mt-1.5 h-10 rounded-lg" /></div>
              <div><Label>Role / title</Label><Input data-testid="staff-role" value={form.role_title} onChange={set("role_title")} className="mt-1.5 h-10 rounded-lg" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1.5 h-10 rounded-lg" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5 h-10 rounded-lg" /></div>
            </div>
            <div><Label>Photo</Label><div className="mt-1.5"><ImageUploader value={form.photo_url} onChange={(url) => setForm((f) => ({ ...f, photo_url: url }))} testid="staff-photo-input" shape="round" /></div></div>
            <div><Label>Bio</Label><Textarea data-testid="staff-bio" value={form.bio} onChange={set("bio")} className="mt-1.5 rounded-lg" rows={2} placeholder="Short intro shown on your public booking page" /></div>
            <div>
              <Label>Calendar colour</Label>
              <div className="mt-2 flex gap-2">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setForm((f) => ({ ...f, color: c }))} style={{ background: c }}
                    className={`h-8 w-8 rounded-full border-2 ${form.color === c ? "border-stone-900" : "border-transparent"}`} />
                ))}
              </div>
            </div>
            {!editing && (
              <div><Label>Login password (optional)</Label>
                <Input type="password" data-testid="staff-password" value={form.login_password} onChange={set("login_password")} className="mt-1.5 h-10 rounded-lg" placeholder="Give staff their own login" />
                <p className="text-xs text-stone-500 mt-1">Set with the email above to let them sign in to their own calendar.</p>
              </div>
            )}
            <div>
              <Label>Working hours</Label>
              {locked ? <p data-testid="staff-hours-synced-note" className="text-xs text-amber-800 bg-amber-50 rounded-md px-2 py-1.5 mt-1">These hours follow your salon hours (Settings → Hours). Turn off "Same hours for all staff" there to set personal hours. Breaks can still be set here.</p>
                : soloSync ? <p data-testid="staff-hours-solo-note" className="text-xs text-stone-600 bg-stone-100 rounded-md px-2 py-1.5 mt-1">You're the only team member, so changing these hours also updates your salon hours.</p> : null}
              <p className="text-xs text-stone-500 mt-0.5">Evening hours fill up in order: clients can only pick a time next to an existing evening booking, so there are no gaps.</p>
              <div className="mt-2 space-y-1.5">
                {DAYS_SHORT.map((day, d) => (
                  <div key={d} className="space-y-1">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="w-10 font-medium">{day}</span>
                    <Switch disabled={locked} checked={!form.working_hours[d].off} onCheckedChange={(v) => setWork(d, "off", !v)} data-testid={`staff-day-${d}`} />
                    {form.working_hours[d].off ? <span className="text-stone-400 text-xs">Off</span> : (
                      <>
                        <input type="time" disabled={locked} value={form.working_hours[d].start} onChange={(e) => setWork(d, "start", e.target.value)} className="border border-stone-300 rounded-md px-1.5 py-1 text-xs" />
                        <span className="text-stone-400">–</span>
                        <input type="time" disabled={locked} value={form.working_hours[d].end} onChange={(e) => setWork(d, "end", e.target.value)} className="border border-stone-300 rounded-md px-1.5 py-1 text-xs" />
                        <button type="button" data-testid={`staff-add-break-${d}`} onClick={() => addBreak(d)} className="text-xs text-stone-600 underline ml-1">+ break</button>
                        <span className="flex items-center gap-1.5 sm:ml-2 sm:pl-2 sm:border-l border-stone-200 basis-full sm:basis-auto pl-0 sm:pl-2" data-testid={`staff-evening-${d}`}>
                          <Switch disabled={locked} checked={!!eveningOf(d).on} onCheckedChange={(v) => setEvening(d, "on", v)} data-testid={`staff-evening-toggle-${d}`} />
                          <span className="text-xs text-stone-500">Evening</span>
                          {eveningOf(d).on && (
                            <>
                              <input type="time" disabled={locked} data-testid={`staff-evening-start-${d}`} value={eveningOf(d).start} onChange={(e) => setEvening(d, "start", e.target.value)} className="border border-stone-300 rounded-md px-1.5 py-1 text-xs" />
                              <span className="text-stone-400">–</span>
                              <input type="time" disabled={locked} data-testid={`staff-evening-end-${d}`} value={eveningOf(d).end} onChange={(e) => setEvening(d, "end", e.target.value)} className="border border-stone-300 rounded-md px-1.5 py-1 text-xs" />
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                  {!form.working_hours[d].off && breaksOf(d).map((b, i) => (
                    <div key={`${d}-${i}`} className="flex items-center gap-2 text-xs pl-[5.5rem] text-stone-600" data-testid={`staff-break-${d}-${i}`}>
                      <span>Break</span>
                      <input type="time" value={b.start} onChange={(e) => editBreak(d, i, "start", e.target.value)} className="border border-stone-300 rounded-md px-1.5 py-1" />
                      <span className="text-stone-400">–</span>
                      <input type="time" value={b.end} onChange={(e) => editBreak(d, i, "end", e.target.value)} className="border border-stone-300 rounded-md px-1.5 py-1" />
                      <button type="button" aria-label="Remove break" data-testid={`staff-remove-break-${d}-${i}`} onClick={() => setBreaks(d, breaksOf(d).filter((_, j) => j !== i))} className="text-rose-600 px-1">×</button>
                    </div>
                  ))}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Days off</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <input type="date" value={dayOff} onChange={(e) => setDayOff(e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1.5 text-sm" />
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addDayOff}>Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.days_off.map((d) => (
                  <span key={d} className="text-xs bg-stone-100 rounded-full px-2 py-1 flex items-center gap-1">{d}
                    <button onClick={() => setForm((f) => ({ ...f, days_off: f.days_off.filter((x) => x !== d) }))}>×</button></span>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="staff-active" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-staff-button" onClick={save} disabled={!form.name} className="rounded-full bg-stone-900 hover:bg-stone-800">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
